<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomerStore } from '@/stores/customer.js';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useServerTable } from '@/composables/useServerTable.js';


const props = defineProps({
  search: { type: String, default: '' },
  isActive: { type: Boolean, required: false, default: undefined },
  responsible: { type: [String, Number], required: false, default: undefined }
});

const emit = defineEmits(['initial-count-loaded']);
const initialLoadEmitted = ref(false);


const customerStore = useCustomerStore();
const router = useRouter();

const {
  data: customers,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  sortField,
  sortOrder,
  setParams,
  load,
  setSearch,
  setExtra,
  onPageChange,
  onSort,
  firstIndex
} = useServerTable({
  initialRows: 10,
  debounceMs: 800,
  storageKey: 'table.customers.pageSize',
  loadPage: async ({ page, rows, search, ...extra }) => {
    return await customerStore.loadCustomers({
      page,
      page_size: rows,
      search,
      is_active: typeof extra.is_active === 'boolean' ? extra.is_active : undefined,
      ordering: extra.ordering,
      responsible: extra.responsible
    });
  }
});

const goToClient = async (event) => {
  if (event.data && event.data.id) {
    await router.push(`/customers/${event.data.id}`);
  }
};

watch(
    () => [props.search, props.isActive, props.responsible],
    (newVals, oldVals) => {
      const isInitialLoad = oldVals === undefined; // Определяем, первый ли это запуск
      const [search, isActive, responsible] = newVals || [];

      // Старую проверку на отсутствие изменений можно оставить
      if (!isInitialLoad) {
        const [prevSearch, prevIsActive, prevResponsible] = oldVals || [];
        if (search === prevSearch && isActive === prevIsActive && responsible === prevResponsible) {
          return;
        }
      }

      setParams({
        search: (search || '').trim(),
        extra: {
          is_active: typeof isActive === 'boolean' ? isActive : undefined,
          responsible
        },
        resetPage: true,
        immediate: isInitialLoad, // Первая загрузка - немедленно, остальные - с debounce
      });
    },
    { immediate: true }
);

// Эмитим count после первой загрузки
watch(totalRecords, (count) => {
  if (!initialLoadEmitted.value && count !== null) {
    initialLoadEmitted.value = true;
    emit('initial-count-loaded', { count, isActive: props.isActive });
  }
});

</script>


<template>
  <DataTable :value="customers"
             stripedRows
             lazy
             paginator
             scrollable
             scrollHeight="flex"
             :rows="rowsPerPage"
             :rowsPerPageOptions="[10, 20, 30]"
             :totalRecords="totalRecords"
             :loading="loading"
             :first="firstIndex()"
             :sortField="sortField"
             :sortOrder="sortOrder"
             @page="onPageChange"
             @sort="onSort"
             class="cursor-pointer"
             @row-click="goToClient"
  >
    <Column
        field="short_name"
        header="Название"
        sortable
        :headerClass="'w-full lg:w-2/5'"
        :bodyClass="'w-full lg:w-2/5'"
    ></Column>
    <Column
        field="facilities_count"
        header="Объекты"
        sortable
        style="width: 10%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    ></Column>
    <Column
        field="works_count"
        header="Работы"
        sortable
        style="width: 10%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    ></Column>

    <Column
        field="primary_responsible_full_name"
        header="Ответственный"
        sortable
        style="width: 20%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        {{ data.primary_responsible_full_name }}
      </template>
    </Column>
  </DataTable>
</template>
